"use client";

import { OrderType } from "@/constants/enum";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import getOrderEstimate from "@/dataLayer/getOrderEstimate";
import { Address } from "@/dto/address";
import { ScheduledTime } from "@/dto/scheduledTime";
import {
  saveToSessionStorage,
  getFromSessionStorage,
} from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { isEmpty } from "lodash";
import { useEffect, useRef } from "react";

interface UseOrderEstimatePollingParams {
  merchantId: string;
  isRetailMerchant: boolean;
  retailPickupDateTimeMillis?: number;
  isRetailCartOpen: boolean;
  isRestaurantCartOpen: boolean;
}

/** Keeps delivery order estimates current while a cart drawer is open. */
export const useOrderEstimatePolling = ({
  merchantId,
  isRetailMerchant,
  retailPickupDateTimeMillis,
  isRetailCartOpen,
  isRestaurantCartOpen,
}: UseOrderEstimatePollingParams): void => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined
  );

  useEffect(() => {
    const updateOrderEstimates = async () => {
      const deliveryAddress = getFromLocalStorage<Partial<Address>>(
        LocalStorageKey.DELIVERY_ADDRESS,
        {}
      );
      const orderEstimates = await getOrderEstimate(
        merchantId,
        deliveryAddress,
        isRetailMerchant ? retailPickupDateTimeMillis : undefined
      );
      saveToSessionStorage(SessionStorageKey.ORDER_ESTIMATES, orderEstimates);
    };
    const deliveryAddress = getFromLocalStorage<Partial<Address>>(
      LocalStorageKey.DELIVERY_ADDRESS,
      {}
    );
    const sessionTime = getFromSessionStorage<Partial<ScheduledTime>>(
      SessionStorageKey.SCHEDULED_TIME,
      {}
    );

    const savedOrderType = sessionTime.orderType ?? null;
    if (
      (isRetailCartOpen || isRestaurantCartOpen) &&
      !isEmpty(deliveryAddress) &&
      savedOrderType === OrderType.DELIVERY &&
      !intervalRef.current
    ) {
      updateOrderEstimates();
      intervalRef.current = setInterval(updateOrderEstimates, 60 * 1000);
    } else {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  }, [isRetailCartOpen, isRestaurantCartOpen]);
};
